import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Swords, Search, Plus, Pencil, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageLoader, PageHeader, Modal, Btn, Field, TextInput, ConfirmButton, ExpandableText } from "@/components/common";
import { RARITY_COLORS } from "@/lib/dnd";

const TYPES = ["", "Weapon", "Armor", "Adventuring Gear", "Tools", "Mounts and Vehicles", "Wondrous Items", "Potion", "Ring", "Rod", "Scroll", "Staff", "Wand"];
const blank = { name: "", type: "Weapon", rarity: "Common", cost: "", weight: 0, description: "" };

export default function Items() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [addFor, setAddFor] = useState(null);
  const [editor, setEditor] = useState(null); // null | {item}

  const { data: items, isLoading } = useQuery({
    queryKey: ["items", search, type],
    queryFn: async () => (await api.get("/items", { params: { search, type } })).data,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["items"] });
  const create = useMutation({
    mutationFn: async (p) => (await api.post("/items", p)).data,
    onSuccess: () => { refresh(); setEditor(null); toast.success("Custom item created!"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const update = useMutation({
    mutationFn: async ({ id, ...p }) => (await api.put(`/items/${id}`, p)).data,
    onSuccess: () => { refresh(); setEditor(null); toast.success("Item updated"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/items/${id}`)).data,
    onSuccess: () => { refresh(); toast.success("Item deleted"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <div>
      <PageHeader title="Items & Gear" subtitle="SRD equipment plus your own custom items" icon={Swords}
        action={<Btn data-testid="create-item-btn" onClick={() => setEditor({ ...blank })}><Wand2 size={16} /> Create Item</Btn>} />

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="item-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search items…" className="input-dark w-full rounded-xl pl-11 pr-4 py-2.5 text-sm" />
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {TYPES.map((t) => (
            <button key={t} data-testid={`type-${t || "all"}`} onClick={() => setType(t)} className={`px-3.5 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition ${type === t ? "btn-violet" : "glass glass-hover text-zinc-400"}`}>{t || "All"}</button>
          ))}
        </div>
      </div>

      {isLoading ? <PageLoader /> : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {items?.map((it) => (
            <div key={it.id} className={`glass glass-hover rounded-2xl p-4 ${it.custom ? "border-violet-500/30" : ""}`} data-testid={`item-${it.id}`}>
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <span className="font-semibold text-white flex items-center gap-1.5">{it.name}{it.custom && <span className="text-[9px] uppercase bg-violet-500/20 text-violet-300 px-1.5 py-0.5 rounded">custom</span>}</span>
                <div className="flex gap-1 shrink-0">
                  {it.custom && <>
                    <button onClick={() => setEditor({ ...it })} data-testid={`edit-item-${it.id}`} className="p-1.5 rounded-lg glass-hover text-zinc-400"><Pencil size={14} /></button>
                    <ConfirmButton onConfirm={() => del.mutate(it.id)} title="Delete item?" message={`Remove "${it.name}" from your library?`} className="p-1.5 rounded-lg text-zinc-500 hover:text-rose-400"><Trash2 size={14} /></ConfirmButton>
                  </>}
                  <button onClick={() => setAddFor(it)} data-testid={`add-item-${it.id}`} className="p-1.5 rounded-lg bg-violet-500/15 text-violet-300 hover:bg-violet-500/25"><Plus size={15} /></button>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${RARITY_COLORS[it.rarity]}22`, color: RARITY_COLORS[it.rarity] }}>{it.rarity}</span>
                <span className="text-xs text-zinc-500">{it.type}</span>
                {it.cost && it.cost !== "—" && <span className="text-xs text-amber-400/80 ml-auto">{it.cost}</span>}
              </div>
              <ExpandableText text={it.description} lines={4} className="text-sm text-zinc-400 leading-relaxed" />
            </div>
          ))}
          {items?.length === 0 && <p className="text-zinc-600 col-span-full text-center py-12">No items found.</p>}
        </div>
      )}

      {/* Create / edit custom item */}
      <Modal open={!!editor} onClose={() => setEditor(null)} title={editor?.id ? "Edit Custom Item" : "Create Custom Item"}>
        {editor && (
          <div className="space-y-4">
            <Field label="Name"><TextInput value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} data-testid="custom-item-name" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Type">
                <select value={editor.type} onChange={(e) => setEditor({ ...editor, type: e.target.value })} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm">
                  {TYPES.filter(Boolean).map((t) => <option key={t} className="bg-zinc-900">{t}</option>)}
                </select>
              </Field>
              <Field label="Rarity">
                <select value={editor.rarity} onChange={(e) => setEditor({ ...editor, rarity: e.target.value })} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm">
                  {Object.keys(RARITY_COLORS).map((r) => <option key={r} className="bg-zinc-900">{r}</option>)}
                </select>
              </Field>
              <Field label="Price"><TextInput value={editor.cost} onChange={(e) => setEditor({ ...editor, cost: e.target.value })} placeholder="e.g. 50 gp" data-testid="custom-item-price" /></Field>
              <Field label="Weight (lb)"><TextInput type="number" value={editor.weight} onChange={(e) => setEditor({ ...editor, weight: Number(e.target.value) })} /></Field>
            </div>
            <Field label="Description"><textarea value={editor.description} onChange={(e) => setEditor({ ...editor, description: e.target.value })} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm min-h-[90px]" /></Field>
            <Btn className="w-full" data-testid="save-custom-item"
              onClick={() => { if (!editor.name.trim()) return toast.error("Name required"); editor.id ? update.mutate(editor) : create.mutate(editor); }}>
              {editor.id ? "Save Changes" : "Create Item"}
            </Btn>
          </div>
        )}
      </Modal>

      <AddItemToCharacter item={addFor} onClose={() => setAddFor(null)} />
    </div>
  );
}

function AddItemToCharacter({ item, onClose }) {
  const qc = useQueryClient();
  const { data: chars } = useQuery({ queryKey: ["characters"], queryFn: async () => (await api.get("/characters")).data, enabled: !!item });
  const add = async (c) => {
    try {
      const fresh = (await api.get(`/characters/${c.id}`)).data;
      const entry = { id: crypto.randomUUID(), name: item.name, quantity: 1, type: item.type, rarity: item.rarity, description: item.description };
      await api.put(`/characters/${c.id}`, { inventory: [...(fresh.inventory || []), entry] });
      qc.invalidateQueries({ queryKey: ["character", c.id] });
      qc.invalidateQueries({ queryKey: ["characters"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(`${item.name} added to ${c.name}`);
      onClose();
    } catch { toast.error("Failed to add item"); }
  };
  return (
    <Modal open={!!item} onClose={onClose} title={item ? `Add "${item.name}" to…` : ""}>
      {chars?.length ? (
        <div className="space-y-2">
          {chars.map((c) => (
            <button key={c.id} onClick={() => add(c)} data-testid={`add-item-to-${c.id}`} className="w-full glass glass-hover rounded-2xl p-3 flex items-center gap-3 text-left">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-violet-900 flex items-center justify-center font-display font-bold">{c.name?.[0]?.toUpperCase()}</div>
              <div className="flex-1"><div className="font-semibold text-white">{c.name}</div><div className="text-xs text-zinc-500">Lvl {c.level} {c.class}</div></div>
              <Plus size={16} className="text-violet-300" />
            </button>
          ))}
        </div>
      ) : <p className="text-sm text-zinc-500 text-center py-6">Create a character first.</p>}
    </Modal>
  );
}
